import React, { useState, useCallback, useRef } from 'react'
import {
  Box,
  Button,
  Stack,
  Tooltip,
  Paper,
  Typography,
  Alert,
} from '@mui/material'
import { Table } from 'Common/MUITable/MUITable'
import { GridColDef } from '@mui/x-data-grid'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'

interface MappingTabContentProps {
  requiredMappingRows: Array<Record<string, unknown>>
  optionalMappingRows: Array<Record<string, unknown>>
  mappingColumns: GridColDef[]
  onRequiredMappingUpdate?: (
    updatedRows: Array<Record<string, unknown>>
  ) => void
  onOptionalMappingUpdate?: (
    updatedRows: Array<Record<string, unknown>>
  ) => void
  onMappingReset?: () => void
}

export const MappingTabContent: React.FC<MappingTabContentProps> = ({
  requiredMappingRows,
  optionalMappingRows,
  mappingColumns,
  onRequiredMappingUpdate,
  onOptionalMappingUpdate,
  onMappingReset,
}) => {
  // State to manage the current editable mapping rows for both required and optional
  const [currentRequiredMappingRows, setCurrentRequiredMappingRows] =
    useState(requiredMappingRows)
  const [currentOptionalMappingRows, setCurrentOptionalMappingRows] =
    useState(optionalMappingRows)

  // Keep the passed in mappingRows in refs so we know when they change
  const previousRequiredMappingRowsRef = useRef(requiredMappingRows)
  const previousOptionalMappingRowsRef = useRef(optionalMappingRows)

  // Use refs to store the original mapping rows for reset functionality
  const originalRequiredMappingRows =
    useRef<Array<Record<string, unknown>>>(requiredMappingRows)
  const originalOptionalMappingRows =
    useRef<Array<Record<string, unknown>>>(optionalMappingRows)

  // Update local state when mappingRows props change (from parent)
  React.useEffect(() => {
    // Only update if the requiredMappingRows have actually changed
    if (
      JSON.stringify(requiredMappingRows) !==
      JSON.stringify(previousRequiredMappingRowsRef.current)
    ) {
      setCurrentRequiredMappingRows(requiredMappingRows)
      previousRequiredMappingRowsRef.current = requiredMappingRows
    }
  }, [requiredMappingRows])

  React.useEffect(() => {
    // Only update if the optionalMappingRows have actually changed
    if (
      JSON.stringify(optionalMappingRows) !==
      JSON.stringify(previousOptionalMappingRowsRef.current)
    ) {
      setCurrentOptionalMappingRows(optionalMappingRows)
      previousOptionalMappingRowsRef.current = optionalMappingRows
    }
  }, [optionalMappingRows])

  // Calculate validation status for required mapping
  const requiredMappingValid = React.useMemo(() => {
    return currentRequiredMappingRows.every(
      (row) =>
        row.source !== null && row.source !== undefined && row.source !== ''
    )
  }, [currentRequiredMappingRows])

  // Method to reset mapping to original state
  const handleResetMapping = useCallback(() => {
    // Reset both mapping rows to their original state using the ref values
    setCurrentRequiredMappingRows([...originalRequiredMappingRows.current])
    setCurrentOptionalMappingRows([...originalOptionalMappingRows.current])

    // Also call the parent reset handler if provided
    if (onMappingReset) {
      onMappingReset()
    }

    console.log('Reset mapping to original state')
  }, [onMappingReset, originalRequiredMappingRows, originalOptionalMappingRows])

  // Process row updates for required mapping
  const processRequiredRowUpdate = (newRow: Record<string, unknown>) => {
    console.log('Required row updated:', newRow)

    const updatedRows = currentRequiredMappingRows.map((row) => {
      if (row.id === newRow.id) {
        return {
          ...row,
          source: newRow.source,
        }
      }
      return row
    })

    // Call the update handler with the latest rows
    if (onRequiredMappingUpdate) {
      onRequiredMappingUpdate(updatedRows)
    }

    // Update local state
    setCurrentRequiredMappingRows(updatedRows)

    return newRow
  }

  // Process row updates for optional mapping
  const processOptionalRowUpdate = (newRow: Record<string, unknown>) => {
    console.log('Optional row updated:', newRow)

    const updatedRows = currentOptionalMappingRows.map((row) => {
      if (row.id === newRow.id) {
        return {
          ...row,
          source: newRow.source,
        }
      }
      return row
    })

    // Call the update handler with the latest rows
    if (onOptionalMappingUpdate) {
      onOptionalMappingUpdate(updatedRows)
    }

    // Update local state
    setCurrentOptionalMappingRows(updatedRows)

    return newRow
  }
  return (
    <Box display="flex" flexDirection="column" width="100%" height="100%">
      {/* Action Buttons */}
      <Box mb={4}>
        <Paper elevation={0} sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.02)' }}>
          <Stack
            direction="row"
            spacing={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={4} alignItems="center">
              {/* Validation Status */}
              {requiredMappingValid ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="body2" color="success.main">
                    All required columns mapped
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={2} alignItems="center">
                  <ErrorIcon color="error" fontSize="small" />
                  <Typography variant="body2" color="error.main">
                    {
                      currentRequiredMappingRows.filter((row) => !row.source)
                        .length
                    }{' '}
                    required column(s) need mapping
                  </Typography>
                </Stack>
              )}
            </Stack>

            <Tooltip title="Reset to original mapping">
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestartAltIcon />}
                onClick={handleResetMapping}
              >
                Reset
              </Button>
            </Tooltip>
          </Stack>
        </Paper>
      </Box>

      {/* Validation Alert */}
      {!requiredMappingValid && (
        <Box mb={4}>
          <Alert severity="warning">
            Please map all required columns before proceeding to the upload
            step.
          </Alert>
        </Box>
      )}

      {/* Required Mapping Table */}
      {currentRequiredMappingRows.length > 0 && (
        <Box mb={6}>
          <Paper elevation={1} sx={{ p: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              color="error.main"
              sx={{ fontWeight: 600 }}
            >
              Required Columns *
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              These columns must be mapped to proceed with the upload.
            </Typography>
            <Box height={300}>
              <Table
                rows={currentRequiredMappingRows}
                columns={mappingColumns.map((col) => {
                  return col
                })}
                showCellVerticalBorder
                showColumnVerticalBorder
                pagination
                initialState={{
                  density: 'compact',
                }}
                editMode="cell"
                processRowUpdate={processRequiredRowUpdate}
                sx={{
                  // Required column styling
                  '& .required-empty': {
                    backgroundColor: 'var(--state-failed-tint) !important', // Red background for empty required cells
                    border: '2px solid var(--state-failed) !important',
                  },
                  '& .required-mapped': {
                    backgroundColor: 'var(--state-complete-tint) !important', // Green background for mapped required cells
                  },
                  '& .required-header-cell': {
                    backgroundColor: 'var(--state-failed-tint) !important',
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-cell--editable': {
                    '&:hover': {
                      backgroundColor: 'var(--brand-blue-50)',
                    },
                  },
                  // Required cell indicator
                  '& .MuiDataGrid-cell[data-field="source"]': {
                    position: 'relative',
                    '&::after': {
                      content: '"*"',
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      color: 'var(--state-failed-fg)',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>
      )}

      {/* Optional Mapping Table */}
      {currentOptionalMappingRows.length > 0 && (
        <Box>
          <Paper elevation={1} sx={{ p: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              color="primary.main"
              sx={{ fontWeight: 600 }}
            >
              Optional Columns
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              These columns can be mapped if available in your CSV file.
              Unmapped optional columns will be ignored.
            </Typography>
            <Box height={300}>
              <Table
                rows={currentOptionalMappingRows}
                columns={mappingColumns.map((col) => {
                  return col
                })}
                showCellVerticalBorder
                showColumnVerticalBorder
                pagination
                initialState={{
                  density: 'compact',
                }}
                editMode="cell"
                processRowUpdate={processOptionalRowUpdate}
                sx={{
                  // Optional column styling
                  '& .optional-empty': {
                    backgroundColor: 'var(--surface-50) !important', // Light gray for unmapped optional
                  },
                  '& .optional-mapped': {
                    backgroundColor: 'var(--state-complete-tint) !important', // Light green for mapped optional
                  },
                  '& .optional-header-cell': {
                    backgroundColor: 'var(--brand-blue-50) !important',
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-cell--editable': {
                    '&:hover': {
                      backgroundColor: 'var(--brand-blue-50)',
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  )
}

export default MappingTabContent
